import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { authService } from "../services/auth";
import { subscriptionService } from "../services/subscription";
import { benefitsService } from "../services/benefits";
import { BENEFICIO_ELEGIDO } from "./ElegirBeneficio";
import { toast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import LandingNavbar from "../components/ui/LandingNavbar";
import VerificarCorreo from "../components/VerificarCorreo";
import { leerCodigo, EVENTO_CODIGO } from "../lib/closerCode";
import { leerEnlace } from "../lib/enlace";
import { VD, GRAD, textoDegradado } from "../lib/brand";

const Auth = ({ mode = "login" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // Código de closer validado (llega por /signup?code=X desde el link/QR de venta)
  const [closerCode, setCloserCode] = useState(null);
  /*
    Paso intermedio del camino gratis: la cuenta ya existe pero el correo no
    está verificado. Sin verificar, el backend niega el desbloqueo (403
    CORREO_NO_VERIFICADO) — es el anti-abuso de los descuentos de bienvenida.
  */
  const [verificandoCorreo, setVerificandoCorreo] = useState(false);

  /*
    El código del closer solo se **enseña** aquí; ya no se gestiona.

    Lo captura y lo guarda un hook global en cuanto alguien abre un enlace con
    `?code=`, en cualquier ruta — antes solo lo leía esta pantalla, así que si
    el closer mandaba su enlace apuntando a la portada, su venta se perdía. En
    el registro se muestra únicamente para que la persona vea que su descuento
    sigue en pie; quien lo aplica es el checkout.
  */
  useEffect(() => {
    if (mode !== "signup") return;

    const mirar = () => {
      const guardado = leerCodigo();
      if (guardado?.code) setCloserCode(guardado);
    };

    mirar();
    /* El código puede llegar después: se valida contra el servidor mientras
       esta pantalla ya está montada. */
    window.addEventListener(EVENTO_CODIGO, mirar);
    return () => window.removeEventListener(EVENTO_CODIGO, mirar);
  }, [mode]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await authService.login({
        email: formData.email,
        password: formData.password,
      });

      if (response.success) {
        const user = response.data.user;
        toast({
          title: "¡Bienvenido!",
          description: `Hola ${user.name}`,
        });

        if (user.role === "admin") {
          navigate("/admin");
        } else if (user.role === "closer") {
          navigate("/closer");
        } else if (user.role === "tienda") {
          navigate("/home");
        } else {
          /*
            Flujo freemium (Módulo 1): al entrar, TODOS los clientes van a la
            zona de miembros.

            Antes se consultaba la suscripción aquí y a quien no pagaba se le
            mandaba directo a /subscription. Ese era el segundo muro —además
            del que ya se quitó en la ruta de /home— y dejaba al usuario
            gratuito sin poder ver siquiera sus beneficios ni su ahorro.

            El muro ya no vive en la puerta: vive en cada tarjeta premium, con
            su candado, y aparece al intentar desbloquear el segundo beneficio.
          */
          /* Salvo que viniera de otra pantalla: desde el checkout, «Ya tengo
             cuenta» tiene que devolverlo al checkout para terminar de pagar. */
          navigate(location.state?.from?.pathname || "/home", { replace: true });
        }
      }
    } catch (err) {
      const errorMessage =
        err.message || "Error al iniciar sesión. Verifica tus credenciales.";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Validación de los datos de la cuenta. Corre ANTES de tokenizar la tarjeta y
  // antes de cobrar; si algo falta, no se crea cuenta ni se cobra nada.
  const validateSignup = () => {
    if (
      !formData.name ||
      !formData.email ||
      !formData.phone ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      setError("Por favor completa todos los campos");
      return false;
    }
    if (formData.password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Las contraseñas no coinciden");
      return false;
    }
    if (!acceptedTerms) {
      setError("Debes aceptar los términos y condiciones y la política de privacidad para continuar");
      return false;
    }
    setError(null);
    return true;
  };

  /*
    Registro y, después, checkout. Aquí ya no se cobra.

    Antes esta pantalla creaba la cuenta **y** cobraba en la misma acción, con
    una segunda pantalla de cobro distinta en `/subscription`. Iván pidió que el
    flujo de checkout sea siempre el mismo (13-ago-2026), y lo era en intención
    pero no en pantalla: quien llegaba por el enlace de un closer veía un
    formulario de planes que no se parecía a la pantalla de pago del mockup.

    Separarlo arregla además un fallo que estaba latente: `authService.register`
    guarda el token en cuanto la cuenta existe. Si el registro salía bien y el
    cobro fallaba, la persona quedaba **con cuenta y sesión** en una pantalla de
    pago a medias; al recargar entraba a la app sin haber pagado, y el closer se
    quedaba sin su comisión. El `accountCreatedRef` que evitaba duplicar la
    cuenta era el parche de ese estado a medias; ya no hace falta.

    Ahora: se crea la cuenta y se va al checkout, que es el único sitio donde se
    cobra. El código del closer viaja aparte, guardado al abrir su enlace.
  */
  const handleSignup = async (e) => {
    e?.preventDefault?.();

    if (!validateSignup()) return;

    setLoading(true);
    setError(null);

    try {
      // El enlace de referido por el que llegó, si llegó por uno: el servidor
      // lo usa para saber a quién contar la cuenta.
      const enlace = leerEnlace()?.codigo;
      const userData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        ...(enlace && { enlace }),
      };

      const registerResponse = await authService.register(userData);
      if (!registerResponse || !registerResponse.success) {
        throw new Error(registerResponse?.error || "Error al registrar usuario");
      }

      const user = registerResponse.data.user;
      if (user.role === "admin") {
        navigate("/admin");
        return;
      }

      /* Al checkout sin pantalla intermedia y con `replace`: la cuenta ya
         existe, así que volver atrás a este formulario no llevaría a ningún
         sitio útil. */
      navigate("/subscription", { replace: true });
    } catch (err) {
      const errorMessage = err.message || "No se pudo crear la cuenta.";
      setError(errorMessage);
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  /*
    ¿Viene del camino del beneficio gratis?

    Si eligió un beneficio en el paso anterior, este registro **no cobra**: crea
    la cuenta, le desbloquea lo que eligió y lo manda a su QR. El cobro llega
    después, cuando quiera el segundo beneficio.

    Es el cambio de fondo del flujo freemium: antes registrarse y pagar eran una
    sola acción indivisible, y no había forma de tener cuenta sin pagar.
  */
  const beneficioElegido = typeof window !== 'undefined'
    ? sessionStorage.getItem(BENEFICIO_ELEGIDO)
    : null;

  /*
    Alta sin cobro, ahora en dos pasos: crear la cuenta, y VERIFICAR EL CORREO
    antes de desbloquear.

    El paso del código es el anti-abuso: el beneficio de bienvenida es un
    descuento real en mostrador y en producción se lo estaban llevando cuentas
    con correos inventados. El backend manda el primer código en el alta misma
    y niega el desbloqueo (403 CORREO_NO_VERIFICADO) hasta que se canjee.
  */
  const handleSignupGratis = async (e) => {
    e.preventDefault();
    if (!validateSignup()) return;

    setLoading(true);
    try {
      const enlace = leerEnlace()?.codigo;
      const registerResponse = await authService.register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        ...(enlace && { enlace }),
      });
      if (!registerResponse?.success) {
        throw new Error(registerResponse?.error || "No se pudo crear tu cuenta");
      }

      // Cuenta creada y código en camino: toca la pantalla de verificación.
      setVerificandoCorreo(true);
    } catch (err) {
      const msg = err.message || "No se pudo crear tu cuenta.";
      setError(msg);
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  /* Correo ya verificado: desbloquear lo que eligió y entrar a su QR. */
  const desbloquearYEntrar = async () => {
    /*
      Desbloquear lo que eligió.

      Antes este fallo se tragaba en silencio, y el resultado era el peor
      posible: la cuenta quedaba creada, el beneficio NO, y el usuario
      aterrizaba en una app donde lo primero que tocaba le pedía pagar —
      justo lo contrario del flujo. Ahora, si falla, se le dice y se le
      manda a elegir otra vez en vez de dejarlo perdido.
    */
    let desbloqueado = true;
    try {
      await benefitsService.unlock(beneficioElegido);
    } catch (_) {
      desbloqueado = false;
    }
    sessionStorage.removeItem(BENEFICIO_ELEGIDO);

    if (!desbloqueado) {
      toast({
        title: "Tu cuenta ya está lista",
        description: "No pudimos activar ese beneficio. Elige otro, sigue siendo gratis.",
      });
      navigate("/home");
      return;
    }

    toast({
      title: "¡Beneficio desbloqueado!",
      description: "Muestra tu QR en tienda para usarlo.",
    });
    navigate("/home?tab=qr");
  };

  /* Campos del manual (§6): fondo claro y borde suave, en los tokens de marca.
     Antes eran los grises de Tailwind, que no son los del sistema. Se conserva
     la píldora porque es la forma que ya usa el checkout. */
  /*
    `/signup` sin beneficio elegido ya no tiene pantalla propia (16-sep-2026).

    Quien viene a comprar crea su cuenta en el checkout mismo. Y los enlaces de
    closer que ya se repartieron apuntaban aquí (`/signup?code=`): esos van al
    enlace corto, que ahora es la entrada al beneficio de bienvenida que el
    closer ofrece — antes se lo saltaban. Con beneficio elegido, esta pantalla
    sigue siendo el registro del camino gratis.
  */
  if (mode === "signup" && !beneficioElegido) {
    const codigo = new URLSearchParams(window.location.search).get("code");
    return <Navigate to={codigo ? `/r/${encodeURIComponent(codigo)}` : "/subscription"} replace />;
  }

  const inputCls = "bg-vd-niebla border border-vd-linea text-vd-tinta placeholder:text-vd-pizarra rounded-full h-14 px-6 text-base font-medium focus-visible:ring-1 focus-visible:ring-vd-naranja focus-visible:ring-offset-0";

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ position: "relative" }}>
      {/*
        En el camino del beneficio gratis la pantalla va limpia, como en
        LOGIN_BENEFICIO_ACCESO: sin barra y sin marquesina. El visitante viene
        de elegir su beneficio y lo único que tiene que hacer aquí es dejar sus
        datos — cualquier otra cosa es una salida de la que puede irse.

        (La marquesina además se veía como cuadros negros: sus logos no tienen
        imagen y el filtro que los fuerza a negro los deja en sólido.)
      */}
      {/*
        La marquesina de marcas se retiró de aquí (Iván, 13-ago-2026: «cuando
        voy a poner mi correo o contraseña, quitar el carrusel de tiendas»).

        Tiene sentido: en el landing la marquesina es argumento —mira quién
        está dentro—, pero sobre un formulario de dos campos es ruido en
        movimiento justo donde la persona necesita concentrarse para teclear.
      */}
      {!beneficioElegido && <LandingNavbar onBack={() => navigate("/")} />}

      {/* Con beneficio elegido, solo una vuelta atrás discreta. Durante la
          verificación no: la cuenta ya existe y volver a elegir beneficio
          desde aquí dejaría el alta a medias. */}
      {beneficioElegido && !verificandoCorreo && (
        <button
          type="button"
          onClick={() => navigate('/beneficios/elegir')}
          aria-label="Volver a elegir beneficio"
          style={{
            position: 'absolute',
            top: 'max(20px, env(safe-area-inset-top, 20px))',
            left: 18,
            width: 40,
            height: 40,
            borderRadius: 9999,
            background: VD.niebla,
            border: 'none',
            color: VD.tinta,
            fontSize: 18,
            cursor: 'pointer',
          }}
        >
          ←
        </button>
      )}

      <div className="flex-1 px-6 py-10 flex flex-col items-center">
        <div className="w-full max-w-md">

          {/* ── LOGIN ─────────────────────────────────── */}
          {mode === "login" && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {/* Display del manual: Poppins Bold. La segunda línea va en el
                  degradado de marca en vez del negro al 40%, que era la forma
                  del sistema viejo de restarle peso a una palabra. */}
              <div className="text-center pt-4">
                <h1
                  className="font-display font-bold text-vd-tinta"
                  style={{ fontSize: 'clamp(32px,7vw,52px)', letterSpacing: '-0.025em', lineHeight: 1.06 }}
                >
                  Bienvenido de<br />
                  <span style={textoDegradado(GRAD.marca)}>vuelta</span>
                </h1>
                <p className="text-vd-pizarra text-[15px] mt-3">
                  Accede a tus beneficios exclusivos
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-3">
                <Input name="email" type="email" value={formData.email}
                  onChange={handleInputChange} placeholder="Correo electrónico"
                  className={inputCls} required />
                <Input name="password" type="password" value={formData.password}
                  onChange={handleInputChange} placeholder="Contraseña"
                  className={inputCls} required />

                {error && (
                  <div className="bg-vd-alerta/8 border border-vd-alerta/25 rounded-2xl p-3">
                    <p className="text-vd-alerta text-sm text-center">{error}</p>
                  </div>
                )}

                {/* Botón primario del manual: degradado vivo y sombra cálida.
                    Era negro sólido, del sistema anterior. */}
                <Button type="submit" disabled={loading} variant="vondi"
                  className="w-full mt-4 h-14 text-base">
                  {loading ? "Cargando..." : "Acceder"}
                </Button>
              </form>

              <div className="flex flex-col items-center gap-3 text-sm">
                <button onClick={() => navigate("/forgot-password")}
                  className="text-vd-pizarra hover:text-vd-tinta transition-colors underline underline-offset-4 decoration-vd-linea">
                  ¿Olvidaste tu contraseña?
                </button>
                <button onClick={() => navigate("/signup")}
                  className="text-vd-pizarra hover:text-vd-tinta transition-colors">
                  {/* Ni «Regístrate» —que el requerimiento pide sustituir— ni
                      «Acceder», que chocaría con el botón de arriba. «Créala
                      gratis» dice además lo que hoy es cierto: el alta no cobra. */}
                  ¿No tienes cuenta?{" "}
                  <span className="text-vd-naranja font-semibold underline underline-offset-4">Créala gratis</span>
                </button>
              </div>
            </div>
          )}

          {/* ── Paso del código (camino gratis): la cuenta ya existe, falta
                 el correo. `autoEnviar` en false: el primer código salió con
                 el alta; aquí solo se canjea o se reenvía. ── */}
          {mode === "signup" && verificandoCorreo && (
            <VerificarCorreo
              correo={formData.email}
              autoEnviar={false}
              onVerificado={desbloquearYEntrar}
              onCancelar={() => {
                /* Puede verificar después: su beneficio lo espera en el
                   catálogo, que le pedirá el código al tocarlo. */
                sessionStorage.removeItem(BENEFICIO_ELEGIDO);
                navigate("/home");
              }}
            />
          )}

          {/* ── SIGNUP — una sola pantalla: cuenta + pago obligatorio ── */}
          {mode === "signup" && !verificandoCorreo && (
            <div className="space-y-7 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {/* Encabezado. Con beneficio elegido reproduce LOGIN_BENEFICIO_ACCESO
                  al pie de la letra: el porqué en negro, la promesa en coral y la
                  consecuencia en gris. */}
              {beneficioElegido ? (
                <div className="text-center pt-4">
                  <h1
                    className="font-display text-vd-tinta"
                    style={{ fontSize: 'clamp(21px,5.4vw,27px)', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.22 }}
                  >
                    Para activar tu beneficio necesitamos crear tu acceso
                  </h1>
                  <p
                    className="font-display"
                    style={{
                      fontSize: 'clamp(28px,7.4vw,38px)',
                      fontWeight: 700,
                      letterSpacing: '-0.025em',
                      lineHeight: 1.12,
                      marginTop: 10,
                      ...textoDegradado(GRAD.marca),
                    }}
                  >
                    Rápido y sin costo
                  </p>
                  <p style={{ color: VD.pizarra, fontSize: 15, lineHeight: 1.45, marginTop: 12 }}>
                    Una vez completando esto accederás de inmediato a tu beneficio
                  </p>
                </div>
              ) : (
                <div className="text-center pt-4">
                  <h1
                    className="font-display font-bold text-vd-tinta"
                    style={{ fontSize: 'clamp(32px,7vw,52px)', letterSpacing: '-0.025em', lineHeight: 1.06 }}
                  >
                    Crea tu<br />cuenta
                  </h1>
                  <p className="text-vd-pizarra text-[15px] mt-3">
                    {/* Decía «en un solo paso», que era verdad cuando esta
                        pantalla cobraba. Ahora el cobro está después, y dejar
                        la promesa vieja haría que el checkout se leyera como
                        un paso que nadie anunció. */}
                    Primero tus datos, después el pago
                  </p>
                </div>
              )}

              {/* Datos de la cuenta (inputs controlados; el envío lo dispara el botón de pago) */}
              <div className="space-y-3">
                <Input name="name" value={formData.name} onChange={handleInputChange}
                  placeholder="Nombre completo" className={inputCls} required />
                <Input name="phone" type="tel" value={formData.phone} onChange={handleInputChange}
                  placeholder="Número telefónico" className={inputCls} required />
                <Input name="email" type="email" value={formData.email} onChange={handleInputChange}
                  placeholder="Correo electrónico" className={inputCls} required />
                <Input name="password" type="password" value={formData.password} onChange={handleInputChange}
                  placeholder="Contraseña" className={inputCls} required minLength={8} />
                <Input name="confirmPassword" type="password" value={formData.confirmPassword}
                  onChange={handleInputChange} placeholder="Confirmar contraseña"
                  className={inputCls} required minLength={8} />
              </div>

              {/*
                Ni planes ni cupón aquí.

                La elección de plan y el cupón viven en el checkout, que es una
                sola pantalla para todo el mundo. Lo que sí se enseña es que el
                descuento del enlace **sigue vivo**: quien llegó por el enlace de
                un closer necesita ver que su descuento no se perdió al
                registrarse, o abandona antes de llegar al cobro.
              */}
              {!beneficioElegido && closerCode && (
                <div className="bg-vd-exito/8 border border-vd-exito/25 rounded-2xl px-4 py-3">
                  <p className="text-vd-exito text-sm text-center font-medium">
                    Código {closerCode.code} aplicado · {closerCode.discount_percent}% de descuento en tu primer pago
                  </p>
                </div>
              )}

              {/* Términos */}
              <div className="flex items-start gap-3 pl-1">
                <Checkbox id="accept-terms" checked={acceptedTerms}
                  onCheckedChange={(c) => setAcceptedTerms(c === true)}
                  className="mt-0.5 rounded-sm border-vd-linea data-[state=checked]:bg-vd-naranja data-[state=checked]:border-vd-naranja" />
                <Label htmlFor="accept-terms" className="cursor-pointer text-xs text-vd-pizarra leading-relaxed">
                  Acepto los{" "}
                  <button type="button" onClick={(e) => { e.preventDefault(); navigate("/terms"); }}
                    className="text-vd-naranja font-semibold underline underline-offset-2">Términos y condiciones</button>
                  {" "}y el{" "}
                  <button type="button" onClick={(e) => { e.preventDefault(); navigate("/privacy"); }}
                    className="text-vd-naranja font-semibold underline underline-offset-2">Aviso de Privacidad</button>
                </Label>
              </div>

              {error && (
                <div className="bg-vd-alerta/8 border border-vd-alerta/25 rounded-2xl p-3">
                  <p className="text-vd-alerta text-sm text-center">{error}</p>
                </div>
              )}

              {/* Camino del beneficio gratis: un solo botón, sin tarjeta. */}
              {beneficioElegido && (
                <button
                  type="button"
                  onClick={handleSignupGratis}
                  disabled={!acceptedTerms || loading}
                  className="bg-grad-rojo-rosa text-white rounded-full font-semibold w-full"
                  style={{
                    height: 56,
                    border: 'none',
                    fontSize: 16,
                    cursor: acceptedTerms && !loading ? 'pointer' : 'not-allowed',
                    opacity: acceptedTerms && !loading ? 1 : 0.55,
                    boxShadow: '0 12px 28px -10px rgba(245,43,22,0.55)',
                  }}
                >
                  {loading ? 'Creando tu cuenta...' : 'Activar Beneficio'}
                </button>
              )}

              {/* Crear la cuenta y seguir al cobro. La tarjeta se pide en el
                  checkout, que es la misma pantalla para todos. */}
              {!beneficioElegido && (
                <button
                  type="button"
                  onClick={handleSignup}
                  disabled={!acceptedTerms || loading}
                  className="bg-grad-rojo-rosa text-white rounded-full font-semibold w-full"
                  style={{
                    height: 56,
                    border: 'none',
                    fontSize: 16,
                    cursor: acceptedTerms && !loading ? 'pointer' : 'not-allowed',
                    opacity: acceptedTerms && !loading ? 1 : 0.55,
                    boxShadow: '0 12px 28px -10px rgba(245,43,22,0.55)',
                  }}
                >
                  {loading ? 'Creando tu cuenta...' : 'Crear cuenta y continuar'}
                </button>
              )}

              <button onClick={() => navigate("/login")}
                className="w-full text-center text-sm text-vd-pizarra hover:text-vd-tinta transition-colors">
                ¿Ya tienes cuenta?{" "}
                <span className="text-vd-naranja font-semibold underline underline-offset-4">Inicia sesión</span>
              </button>
            </div>
          )}

        </div>
      </div>
      <Toaster />
    </div>
  );
};

export default Auth;
